
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, RefreshCcw } from 'lucide-react';
import { NewsCategory, NewsSource } from '@/types/news';
import { newsSources } from '@/services/newsService';

interface NewsFilterProps {
  onFilterChange: (categories: NewsCategory[], sources: string[]) => void;
  availableCategories: Array<{ value: NewsCategory; label: string }>;
}

const NewsFilter: React.FC<NewsFilterProps> = ({
  onFilterChange,
  availableCategories,
}) => {
  const [selectedCategories, setSelectedCategories] = useState<NewsCategory[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>(
    newsSources.filter(s => s.enabled).map(s => s.id)
  );
  const [searchTerm, setSearchTerm] = useState('');

  const handleCategoryChange = (category: NewsCategory, checked: boolean) => {
    setSelectedCategories(prev => 
      checked 
        ? [...prev, category] 
        : prev.filter(c => c !== category)
    );
  };

  const handleSourceChange = (sourceId: string, checked: boolean) => {
    setSelectedSources(prev => 
      checked 
        ? [...prev, sourceId] 
        : prev.filter(s => s !== sourceId)
    );
  };

  const handleApplyFilters = () => {
    onFilterChange(selectedCategories, selectedSources);
  };

  const handleReset = () => {
    setSelectedCategories([]);
    setSelectedSources(newsSources.filter(s => s.enabled).map(s => s.id));
    setSearchTerm('');
    onFilterChange([], newsSources.filter(s => s.enabled).map(s => s.id));
  };

  return (
    <Card className="sticky top-24">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search news..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-3">Categories</h4>
            <div className="space-y-2">
              {availableCategories.map((category) => (
                <div key={category.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`category-${category.value}`}
                    checked={selectedCategories.includes(category.value)}
                    onCheckedChange={(checked) => 
                      handleCategoryChange(category.value, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`category-${category.value}`}
                    className="text-sm cursor-pointer"
                  >
                    {category.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-3">News Sources</h4>
            <div className="space-y-2">
              {newsSources.map((source) => (
                <div key={source.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`source-${source.id}`}
                    checked={selectedSources.includes(source.id)}
                    onCheckedChange={(checked) => 
                      handleSourceChange(source.id, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`source-${source.id}`}
                    className="text-sm cursor-pointer"
                  >
                    {source.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={handleApplyFilters}>Apply Filters</Button>
            <Button 
              variant="outline" 
              onClick={handleReset}
              className="flex items-center gap-1"
            >
              <RefreshCcw className="h-3 w-3" /> Reset Filters
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewsFilter;
